import { FC, useState, useEffect, useRef } from 'react';
import { Stack, Text, Dropdown, IDropdownOption, TextField, ITextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, DefaultButton, ProgressIndicator } from '@fluentui/react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import MasterList from './MasterList';
import { promptService } from './services/promptService';
import { PromptCollection, PromptFile, getRequiredVarsArray, getRequiredVarsKeys } from './types';
import { useAuthErrorContext } from '../../components/AuthErrorProvider';
import { processTeamsTranscriptStream } from '../../api/api';
import styles from './PromptAdjuster.module.scss';

const PromptAdjuster: FC = () => {
  const intl = useIntl();
  const { handleAuthError } = useAuthErrorContext();
  const [promptOptions, setPromptOptions] = useState<IDropdownOption[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string | undefined>();
  const [selectedPromptCollection, setSelectedPromptCollection] = useState<PromptCollection | undefined>();
  const [fileTexts, setFileTexts] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCollection, setIsLoadingCollection] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const textAreaRefs = useRef<Record<string, ITextField>>({});
  
  // Teams transcript specific state
  const [transcriptFile, setTranscriptFile] = useState<File | null>(null);
  const [transcriptPreview, setTranscriptPreview] = useState<string>('');
  const [isProcessingTranscript, setIsProcessingTranscript] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingResult, setProcessingResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isTeamsInterviewProcessing = selectedPromptId === 'teams-interview-processing';

  useEffect(() => {
    const loadPromptOptions = async () => {
      try {
        const response = await promptService.getPromptCollectionsForDropdown(undefined, handleAuthError);
        if (response.data) {
          const options: IDropdownOption[] = [
            { key: '', text: intl.formatMessage({ id: 'promptAdjuster.dropdown.placeholder' }) },
            ...response.data.map(collection => ({ key: collection.id, text: collection.name }))
          ];
          setPromptOptions(options);
        } else if (response.error) {
          console.error('Failed to load prompt options:', response.error);
        }
      } catch (error) {
        console.error('Failed to load prompt options:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPromptOptions();
  }, [intl, handleAuthError]);

  useEffect(() => {
    const loadSelectedPrompt = async () => {
      if (selectedPromptId) {
        setIsLoadingCollection(true);
        try {
          const response = await promptService.getPromptCollection(selectedPromptId, handleAuthError);
          if (response.data) {
            const collection = response.data;
            setSelectedPromptCollection(collection);
            
            // Initialize text content for each file from the API
            const initialTexts: Record<string, string> = {};
            collection.files.forEach(file => {
              const requiredVarsArray = getRequiredVarsArray(file.requiredVars);
              initialTexts[file.name] = file.content || `Sample prompt content for ${file.name}...\n\nUse variables like ${requiredVarsArray.join(', ')}`;
            });
            setFileTexts(initialTexts);
          } else if (response.error) {
            console.error('Failed to load prompt collection:', response.error);
          }
        } catch (error) {
          console.error('Failed to load prompt collection:', error);
        } finally {
          setIsLoadingCollection(false);
        }
      } else {
        setSelectedPromptCollection(undefined);
        setFileTexts({});
        setIsLoadingCollection(false);
      }
    };

    loadSelectedPrompt();
  }, [selectedPromptId, handleAuthError]);

  const handlePromptChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    setSelectedPromptId(option?.key as string);
  };

  const handleFileTextChange = (fileName: string, newValue?: string) => {
    setFileTexts(prev => ({
      ...prev,
      [fileName]: newValue || ''
    }));
  };

  const handleVariableClick = (variable: string, fileName: string) => {
    const currentText = fileTexts[fileName] || '';
    
    // Only insert if the variable is not already present
    if (currentText.includes(variable)) {
      return;
    }

    const textField = textAreaRefs.current[fileName];
    if (!textField) return;

    // Get the current cursor position from the text field
    const selectionStart = textField.selectionStart || 0;
    const selectionEnd = textField.selectionEnd || 0;
    
    // Insert the variable at cursor position using current text
    const newText = currentText.substring(0, selectionStart) + variable + currentText.substring(selectionEnd);
    handleFileTextChange(fileName, newText);
    
    // Set cursor position after the inserted variable
    const newCursorPosition = selectionStart + variable.length;
    
    // Focus back to text field and set cursor position
    setTimeout(() => {
      textField.focus();
      textField.setSelectionStart(newCursorPosition);
      textField.setSelectionEnd(newCursorPosition);
    }, 0);
  };

  const validateFiles = (): string[] => {
    if (!selectedPromptCollection) return [];

    const errors: string[] = [];
    
    selectedPromptCollection.files.forEach(file => {
      const currentText = fileTexts[file.name] || '';
      const requiredVarsArray = getRequiredVarsArray(file.requiredVars);
      const missingVars = requiredVarsArray.filter(variable => !currentText.includes(variable));
      
      if (missingVars.length > 0) {
        errors.push(
          intl.formatMessage(
            { id: 'promptAdjuster.validation.missingVariables' },
            { 
              fileName: file.name, 
              variables: missingVars.join(', ') 
            }
          )
        );
      }
    });

    return errors;
  };

  const handleSave = async () => {
    if (!selectedPromptCollection) return;

    // Validate before saving
    const errors = validateFiles();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous errors
    setValidationErrors([]);

    setIsSaving(true);
    try {
      // Use the new save functionality
      const response = await promptService.savePromptCollection(
        { collectionId: selectedPromptCollection.id, fileTexts }, 
        handleAuthError
      );
      
      if (response.error) {
        console.error('Failed to save prompt collection:', response.error);
        // You might want to show an error message to the user here
      } else {
        toast.success(intl.formatMessage({ id: 'promptAdjuster.toast.success' }));
      }
    } catch (error) {
      console.error('Failed to save prompt collection:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSaving(false);
    }
  };

  const renderRequiredVariables = (file: PromptFile) => {
    const currentText = fileTexts[file.name] || '';
    const requiredVarsKeys = getRequiredVarsKeys(file.requiredVars);
    const requiredVarsValues = getRequiredVarsArray(file.requiredVars);
    
    return (
      <Stack horizontal tokens={{ childrenGap: 8 }} wrap>
        {requiredVarsKeys.map((key, index) => {
          const variable = requiredVarsValues[index];
          const isVariablePresent = currentText.includes(variable);
          
          return (
            <Stack key={key} horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <Text
                onClick={() => handleVariableClick(variable, file.name)}
                className={clsx(styles.variableText, {
                  [styles.present]: isVariablePresent,
                  [styles.missing]: !isVariablePresent
                })}
              >
                {variable}
              </Text>
              <Text className={styles.variableKey}>
                ({key})
              </Text>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const renderFileEditor = (file: PromptFile) => {
    const currentText = fileTexts[file.name] || '';
    const requiredVarsArray = getRequiredVarsArray(file.requiredVars);
    const missingVars = requiredVarsArray.filter(variable => !currentText.includes(variable));

    return (
      <Stack key={file.name} tokens={{ childrenGap: 16 }} className={styles.fileEditor}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          {missingVars.length > 0 && (
            <Text className={styles.missingVarsCount}>
              {intl.formatMessage(
                { id: 'promptAdjuster.validation.missingCount' },
                { count: missingVars.length }
              )}
            </Text>
          )}
        </Stack>
        
        <TextField
          label={file.name}
          multiline
          rows={8}
          autoAdjustHeight={false}
          value={currentText}
          onChange={(_, newValue) => handleFileTextChange(file.name, newValue)}
          componentRef={(ref) => {
            if (ref) {
              textAreaRefs.current[file.name] = ref;
            }
          }}
          styles={{
            field: {
              maxWidth: '100%',
              overflow: 'auto',
              resize: 'vertical'
            }
          }}
        />
        
        <Stack tokens={{ childrenGap: 8 }}>
          <Text variant="small" className={styles.requiredVarsLabel}>
            {intl.formatMessage({ id: 'promptAdjuster.requiredVariables' })}:
          </Text>
          {renderRequiredVariables(file)}
        </Stack>
      </Stack>
    );
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if it's a VTT file
      if (file.name.toLowerCase().endsWith('.vtt') || file.type === 'text/vtt') {
        setTranscriptFile(file);
        loadTranscriptPreview(file);
      } else {
        toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.invalidFileType' }));
      }
    }
  };

  const loadTranscriptPreview = async (file: File) => {
    setIsLoadingPreview(true);
    try {
      const text = await file.text();
      // Show the entire transcript
      setTranscriptPreview(text);
    } catch (error) {
      console.error('Error reading transcript file:', error);
      toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.readError' }));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleRunTranscript = async () => {
    if (!transcriptFile) {
      toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.noFile' }));
      return;
    }

    setIsProcessingTranscript(true);
    setProcessingStatus('');
    setProcessingProgress(0);
    
    try {
      // First, save any prompt changes
      if (selectedPromptCollection) {
        const saveResponse = await promptService.savePromptCollection(
          { collectionId: selectedPromptCollection.id, fileTexts }, 
          handleAuthError
        );
        
        if (saveResponse.error) {
          console.error('Failed to save prompt collection:', saveResponse.error);
          toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.saveError' }));
          return;
        }
      }

      // Read the transcript file
      const transcriptText = await transcriptFile.text();
      
      // Call the streaming transcript processing API
      await processTeamsTranscriptStream(
        { 
          transcript: transcriptText,
          templatePath: "", // Optional - backend will create default document if not provided
          candidateName: "Candidate Name", // This should be extracted from transcript or user input
          interviewerName: "Interviewer Name", // This should be extracted from transcript or user input
          interviewDate: new Date().toISOString()
        },
        (status: string, progress?: number) => {
          setProcessingStatus(status);
          if (progress !== undefined) {
            setProcessingProgress(progress);
          }
        },
        (result: any) => {
          toast.success(intl.formatMessage({ id: 'promptAdjuster.transcript.success' }));
          setProcessingStatus('Complete!');
          setProcessingProgress(100);
          setProcessingResult(result);
          setIsProcessingTranscript(false);
        },
        (error: string) => {
          console.error('Failed to process transcript:', error);
          toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.error' }));
          setProcessingStatus('Error occurred');
          setIsProcessingTranscript(false);
        },
        handleAuthError
      );
    } catch (error) {
      console.error('Error processing transcript:', error);
      toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.error' }));
      setProcessingStatus('Error occurred');
      setIsProcessingTranscript(false);
    }
  };

  const clearTranscript = () => {
    setTranscriptFile(null);
    setTranscriptPreview('');
    setProcessingStatus('');
    setProcessingProgress(0);
    setProcessingResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadResults = () => {
    if (!processingResult) return;

    // Create a filename based on the original transcript file name
    const originalFileName = transcriptFile?.name || 'transcript';
    const baseFileName = originalFileName.replace(/\.vtt$/i, '');
    const fileName = `${baseFileName}_extracted_interview.json`;

    // Create the JSON content
    const jsonContent = JSON.stringify(processingResult, null, 2);
    
    // Create and download the file
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(intl.formatMessage({ id: 'promptAdjuster.transcript.downloadSuccess' }));
  };

  const downloadWordDocument = (documentBytes: string, originalFileName: string) => {
    try {
      // Convert base64 string back to bytes
      const binaryString = atob(documentBytes);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create filename for Word document
      const baseFileName = originalFileName.replace(/\.vtt$/i, '');
      const fileName = `${baseFileName}_interview_report.docx`;
      
      // Create and download the file
      const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(intl.formatMessage({ id: 'promptAdjuster.transcript.wordDownloadSuccess' }));
    } catch (error) {
      console.error('Error downloading Word document:', error);
      toast.error(intl.formatMessage({ id: 'promptAdjuster.transcript.wordDownloadError' }));
    }
  };

  return (
    <MasterList selectedTab="Prompt Adjuster">
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge" className={styles.title}>
          {intl.formatMessage({ id: 'promptAdjuster.title' })}
        </Text>
        
        <Text variant="medium" className={styles.description}>
          {intl.formatMessage({ id: 'promptAdjuster.description' })}
        </Text>

        <Dropdown
          label={intl.formatMessage({ id: 'promptAdjuster.dropdown.label' })}
          placeholder={intl.formatMessage({ id: 'promptAdjuster.dropdown.placeholder' })}
          options={promptOptions}
          selectedKey={selectedPromptId}
          onChange={handlePromptChange}
          disabled={isLoading}
        />
        
        {isLoading && (
          <Stack horizontalAlign="center" verticalAlign="center" className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.medium} label={intl.formatMessage({ id: 'promptAdjuster.loading.collections' })} />
          </Stack>
        )}

        {selectedPromptId && (
          <Stack tokens={{ childrenGap: 16 }}>
            {isLoadingCollection ? (
              <Stack horizontalAlign="center" verticalAlign="center" className={styles.loadingContainer}>
                <Spinner size={SpinnerSize.medium} label={intl.formatMessage({ id: 'promptAdjuster.loading.collection' })} />
              </Stack>
            ) : selectedPromptCollection ? (
              <>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="large" className={styles.collectionTitle}>
                    {selectedPromptCollection.name}
                  </Text>
                  <Text variant="medium" className={styles.collectionDescription}>
                    {selectedPromptCollection.description}
                  </Text>
                </Stack>

                {validationErrors.length > 0 && (
                  <MessageBar messageBarType={MessageBarType.error}>
                    <Stack tokens={{ childrenGap: 4 }}>
                      {validationErrors.map((error, index) => (
                        <Text key={index}>{error}</Text>
                      ))}
                    </Stack>
                  </MessageBar>
                )}

                {isTeamsInterviewProcessing && (
                  <Stack horizontal tokens={{ childrenGap: 24 }} className={styles.teamsLayout}>
                    {/* Left side - Prompts and upload controls */}
                    <Stack tokens={{ childrenGap: 16 }} className={styles.leftPanel}>
                      {/* Prompt editors */}
                      {selectedPromptCollection.files.map(renderFileEditor)}
                      
                      {/* Upload controls at the bottom */}
                      <Stack tokens={{ childrenGap: 12 }}>
                        <Text variant="medium" className={styles.uploadTitle}>
                          {intl.formatMessage({ id: 'promptAdjuster.transcript.uploadTitle' })}
                        </Text>
                        
                        <MessageBar messageBarType={MessageBarType.info}>
                          {intl.formatMessage({ id: 'promptAdjuster.transcript.saveDisclaimer' })}
                        </MessageBar>
                        
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".vtt,text/vtt"
                            onChange={handleFileUpload}
                            className={styles.hiddenFileInput}
                          />
                          <DefaultButton
                            text={intl.formatMessage({ id: 'promptAdjuster.transcript.selectFile' })}
                            onClick={() => fileInputRef.current?.click()}
                            iconProps={{ iconName: 'Upload' }}
                          />
                          {transcriptFile && (
                            <>
                              <DefaultButton
                                text={intl.formatMessage({ id: 'promptAdjuster.transcript.clear' })}
                                onClick={clearTranscript}
                                iconProps={{ iconName: 'Clear' }}
                              />
                              <PrimaryButton
                                text={intl.formatMessage({ id: 'promptAdjuster.transcript.run' })}
                                onClick={handleRunTranscript}
                                disabled={isProcessingTranscript}
                                iconProps={isProcessingTranscript ? { iconName: 'Spinner' } : { iconName: 'Play' }}
                              />
                            </>
                          )}
                        </Stack>

                        {transcriptFile && (
                          <Stack tokens={{ childrenGap: 8 }}>
                            <Text variant="small" className={styles.fileSelected}>
                              {intl.formatMessage(
                                { id: 'promptAdjuster.transcript.fileSelected' },
                                { fileName: transcriptFile.name, size: (transcriptFile.size / 1024).toFixed(1) }
                              )}
                            </Text>
                            
                            {isProcessingTranscript && (
                              <Stack tokens={{ childrenGap: 8 }}>
                                <ProgressIndicator 
                                  percentComplete={processingProgress / 100}
                                  label={processingStatus || intl.formatMessage({ id: 'promptAdjuster.transcript.processing' })}
                                />
                                {processingProgress > 0 && (
                                  <Text variant="small" className={styles.progressText}>
                                    {processingProgress}% complete
                                  </Text>
                                )}
                              </Stack>
                            )}
                            
                            {processingResult && !isProcessingTranscript && (
                              <Stack tokens={{ childrenGap: 8 }}>
                                <MessageBar messageBarType={MessageBarType.success}>
                                  {intl.formatMessage({ id: 'promptAdjuster.transcript.processingComplete' })}
                                </MessageBar>
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                  <DefaultButton
                                    text={intl.formatMessage({ id: 'promptAdjuster.transcript.downloadResults' })}
                                    onClick={downloadResults}
                                    iconProps={{ iconName: 'Download' }}
                                  />
                                  {processingResult.documentBytes && (
                                    <DefaultButton
                                      text={intl.formatMessage({ id: 'promptAdjuster.transcript.downloadWord' })}
                                      onClick={() => downloadWordDocument(processingResult.documentBytes, transcriptFile?.name || 'transcript')}
                                      iconProps={{ iconName: 'WordDocument' }}
                                    />
                                  )}
                                </Stack>
                              </Stack>
                            )}
                          </Stack>
                        )}
                      </Stack>
                      
                      {/* Save button for Teams interview processing */}
                      <PrimaryButton
                        text={intl.formatMessage({ id: 'promptAdjuster.button.save' })}
                        onClick={handleSave}
                        disabled={isSaving}
                        iconProps={isSaving ? { iconName: 'Spinner' } : undefined}
                      />
                    </Stack>

                    {/* Right side - Preview */}
                    <Stack tokens={{ childrenGap: 8 }} className={styles.rightPanel}>
                      <Text variant="medium" className={styles.previewTitle}>
                        {intl.formatMessage({ id: 'promptAdjuster.transcript.previewTitle' })}
                      </Text>
                      
                      {isLoadingPreview ? (
                        <Stack horizontalAlign="center" verticalAlign="center" className={styles.loadingContainer}>
                          <Spinner size={SpinnerSize.small} label={intl.formatMessage({ id: 'promptAdjuster.transcript.loadingPreview' })} />
                        </Stack>
                      ) : transcriptPreview ? (
                        <TextField
                          multiline
                          autoAdjustHeight
                          readOnly
                          value={transcriptPreview}
                          styles={{
                            field: styles.previewContainer
                          }}
                        />
                      ) : (
                        <Stack 
                          horizontalAlign="center" 
                          verticalAlign="center" 
                          className={styles.noPreviewContainer}
                        >
                          <Text className={styles.noPreviewText}>
                            {intl.formatMessage({ id: 'promptAdjuster.transcript.noPreview' })}
                          </Text>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                )}

                {!isTeamsInterviewProcessing && selectedPromptCollection?.files.map(renderFileEditor)}

                {!isTeamsInterviewProcessing && (
                  <PrimaryButton
                    text={intl.formatMessage({ id: 'promptAdjuster.button.save' })}
                    onClick={handleSave}
                    disabled={isSaving}
                    iconProps={isSaving ? { iconName: 'Spinner' } : undefined}
                  />
                )}
              </>
            ) : null}
          </Stack>
        )}
      </Stack>
    </MasterList>
  );
};

export default PromptAdjuster; 